import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { isCanceledError } from 'src/helper/api';
import {
  customizeSubCategory,
  fetchEligibleVendors,
  fetchVendorPreferences,
  resetSubCategoryToDefault,
  setCategoryDefault,
  setSubCategoryOverride,
} from 'src/redux/vendor-preferences/api';
import companyExtraReducers from 'src/redux/vendor-preferences/company-extra-reducer';
// Canonical compound-key builders — shared with selectors.ts so reads and writes
// can't drift to different key shapes.
import { eligibleKey, subKey } from 'src/redux/vendor-preferences/keys';
import type {
  CategoryNode,
  EligibleVendor,
  SubCategoryNode,
  VendorPreferenceError,
  VendorPreferenceIndexResponse,
  VendorPreferenceListResource,
  VendorPreferencesState,
} from 'src/redux/vendor-preferences/types';

// Shape of the `meta.arg` the four write thunks are dispatched with. Only the
// fields the reducers read are typed; callers may pass more (e.g. vendor_uuids).
type WriteArg = {
  market_uuid: string;
  problem_category_uuid: string;
  problem_sub_category_uuid?: string;
};

// Shape of the `meta.arg` `fetchEligibleVendors` is dispatched with — the fields
// the eligible lifecycle reads to derive its per-slot key. `market_uuid` is
// REQUIRED (mirroring WriteArg): the cross-market guard treats a missing/mismatched
// market as stale when a market is active, so an optional field would let a
// market-less dispatch bypass tenant isolation.
type EligibleArg = {
  market_uuid: string;
  problem_category_uuid?: string;
  problem_sub_category_uuid?: string;
};

// A canceled request must NOT surface as a user-facing error. `request()`
// signals a cancel via `thunkAPI.rejectWithValue({ actionType: 'canceled' })`,
// so the sentinel lands on `action.payload` (RTK routes rejectWithValue values
// there and leaves `action.error` as a generic "Rejected"). A raw AbortError
// that never reached the factory would instead show on `action.error`. Check
// both so neither path leaks into error state. (Mirrors the lumi-conversation /
// activity-log slices, which also set error state after this guard; the
// scheduling slice only checks `action.error` but never reads the payload
// sentinel because it sets no error field.)
const isCanceled = (action: { payload?: unknown; error?: unknown }): boolean =>
  isCanceledError(action.payload) || isCanceledError(action.error);

// On a genuine (non-canceled) rejection, `action.payload` carries the
// `{ ...response.data, status }` shape from `request()`'s rejectWithValue.
const errorFromPayload = (payload: unknown): VendorPreferenceError => {
  if (payload && typeof payload === 'object') {
    const { status, message } = payload as { status?: number; message?: string };
    return { status, message };
  }
  return { message: 'Request failed' };
};

// Recompute a single category's overrideCount from its current sub nodes.
const recomputeOverrideCount = (state: VendorPreferencesState, categoryUuid: string) => {
  const category = state.categoriesById[categoryUuid];
  if (!category) return;
  category.overrideCount = category.subCategoryIds.reduce((count, subUuid) => {
    const sub = state.subCategoriesById[subKey(categoryUuid, subUuid)];
    return sub?.state === 'override' ? count + 1 : count;
  }, 0);
};

// Recompute the market-level pending count from the current category statuses.
const recomputePendingCount = (state: VendorPreferencesState) => {
  state.pendingCount = state.categoryOrder.reduce((count, categoryUuid) => {
    return state.categoriesById[categoryUuid]?.status === 'pending' ? count + 1 : count;
  }, 0);
};

// All four write thunks share one type: the value `request()` produces. Capturing
// it via `ReturnType` (rather than hand-typing the generics) keeps `addCase`
// fully typed — pending/fulfilled/rejected actions retain RTK's `meta`/`payload`/
// `error` — without restating `request()`'s intentionally loose payload typing
// here. The caller's `Returned`/`Arg` narrow the fulfilled action in `onFulfilled`.
type WriteThunk = ReturnType<typeof setCategoryDefault>;

// Attach the shared write saving-lifecycle to a thunk:
//   pending   → isSaving = true, saveError = null
//   fulfilled → isSaving = false, then the caller's unique state update
//   rejected  → cancel-guarded no-op, else isSaving = false + saveError
// Only the fulfilled body differs per write, so each write is one call passing
// just that body. `Returned`/`Arg` type the fulfilled action handed to the
// callback; the wire actions stay loosely typed (as elsewhere in this file).
const attachSavingLifecycle = <Returned, Arg>(
  builder: ActionReducerMapBuilder<VendorPreferencesState>,
  thunk: WriteThunk,
  onFulfilled: (
    state: VendorPreferencesState,
    action: PayloadAction<Returned, string, { arg: Arg }>,
  ) => void,
) => {
  builder.addCase(thunk.pending, (state, action) => {
    const arg = action.meta?.arg as WriteArg | undefined;
    if (state.activeMarketUuid && arg?.market_uuid !== state.activeMarketUuid) return;
    state.isSaving = true;
    state.saveError = null;
  });
  builder.addCase(thunk.fulfilled, (state, action) => {
    const arg = action.meta?.arg as WriteArg | undefined;
    if (state.activeMarketUuid && arg?.market_uuid !== state.activeMarketUuid) return;
    state.isSaving = false;
    onFulfilled(state, action as PayloadAction<Returned, string, { arg: Arg }>);
  });
  builder.addCase(thunk.rejected, (state, action) => {
    const arg = action.meta?.arg as WriteArg | undefined;
    if (state.activeMarketUuid && arg?.market_uuid !== state.activeMarketUuid) return;
    if (isCanceled(action)) return;
    state.isSaving = false;
    state.saveError = errorFromPayload(action.payload);
  });
};

const extraReducers = (builder: ActionReducerMapBuilder<VendorPreferencesState>) => {
  // ---- fetch the full preferences tree -----------------------------------
  builder.addCase(fetchVendorPreferences().pending, (state) => {
    state.isConfigLoading = true;
    state.configError = null;
  });

  builder.addCase(fetchVendorPreferences().fulfilled, (state, action) => {
    // The index endpoint returns a BARE tree — `payload` IS the tree, no `.data`.
    const payload = action.payload as VendorPreferenceIndexResponse | undefined;
    if (!payload) {
      state.isConfigLoading = false;
      return;
    }
    // Drop stale responses BEFORE clearing the loading flag: if the UI switched
    // markets (or cleared the selection) since this fetch was dispatched, the
    // payload belongs to a different market and must not overwrite the new
    // market's tree NOR clear its in-flight loading flag. `activeMarketUuid` null
    // (initial mount, or after setActiveMarket(null)) means no market wants this
    // payload, so a payload that names any market is dropped — this is the read
    // path that REPLACES the whole tree, so it must close on a null active market
    // (unlike the write/eligible guards, which are backstopped by node-existence).
    if (payload.market_uuid && payload.market_uuid !== state.activeMarketUuid) return;
    state.isConfigLoading = false;

    const categoriesById: Record<string, CategoryNode> = {};
    const subCategoriesById: Record<string, SubCategoryNode> = {};
    const categoryOrder: string[] = [];

    (payload.categories ?? []).forEach((category) => {
      if (!category?.problem_category_uuid) return;
      const categoryUuid = category.problem_category_uuid;
      const subCategoryIds: string[] = [];

      (category.sub_categories ?? []).forEach((sub) => {
        if (!sub?.problem_sub_category_uuid) return;
        subCategoryIds.push(sub.problem_sub_category_uuid);
        subCategoriesById[subKey(categoryUuid, sub.problem_sub_category_uuid)] = {
          problem_sub_category_uuid: sub.problem_sub_category_uuid,
          problem_category_uuid: categoryUuid,
          label: sub.label,
          slug: sub.slug,
          state: sub.state,
          ranking: sub.vendors ?? [],
        };
      });

      categoriesById[categoryUuid] = {
        problem_category_uuid: categoryUuid,
        label: category.label,
        slug: category.slug,
        status: category.status,
        defaultSource: category.default_source ?? null,
        overrideCount: category.override_count,
        defaultRanking: category.default_vendors ?? [],
        subCategoryIds,
      };
      categoryOrder.push(categoryUuid);
    });

    state.categoriesById = categoriesById;
    state.subCategoriesById = subCategoriesById;
    state.categoryOrder = categoryOrder;
    state.marketUuid = payload.market_uuid ?? state.marketUuid;
    state.pendingCount = payload.pending_count ?? 0;
  });

  builder.addCase(fetchVendorPreferences().rejected, (state, action) => {
    if (isCanceled(action)) return;
    state.isConfigLoading = false;
    state.configError = errorFromPayload(action.payload);
  });

  // ---- fetch eligible (addable) vendors for a slot ------------------------
  // Loading is tracked PER slot (the picker is opened per node; rapid switching
  // means a consumer must know which slot is in flight), keyed the same way as
  // `eligibleVendorsByKey`. The key comes from `action.meta.arg`.
  builder.addCase(fetchEligibleVendors().pending, (state, action) => {
    // Guard BEFORE clearing the error: a stale cross-market pending must not wipe
    // the current market's eligibleError.
    const arg = action.meta?.arg as EligibleArg | undefined;
    if (state.activeMarketUuid && arg?.market_uuid !== state.activeMarketUuid) return;
    state.eligibleError = null;
    if (!arg?.problem_category_uuid) return;
    const key = eligibleKey(arg.problem_category_uuid, arg.problem_sub_category_uuid);
    // A fresh fetch for this slot clears any prior terminal failure so a retry can land.
    delete state.eligibleErrorByKey[key];
    state.eligibleLoadingByKey[key] = true;
  });

  builder.addCase(fetchEligibleVendors().fulfilled, (state, action) => {
    // Like the index endpoint, the BE disables resource wrapping — `payload` IS
    // the bare vendor array, no `.data`. Tolerate a wrapped `{ data: [...] }`
    // shape anyway so a BE-side wrapping change can't silently empty the picker.
    const payload = action.payload as EligibleVendor[] | { data?: EligibleVendor[] } | undefined;
    const data = Array.isArray(payload) ? payload : (payload?.data ?? []);
    const arg = action.meta?.arg as EligibleArg | undefined;
    if (state.activeMarketUuid && arg?.market_uuid !== state.activeMarketUuid) return;
    if (!arg?.problem_category_uuid) return;
    const key = eligibleKey(arg.problem_category_uuid, arg.problem_sub_category_uuid);
    state.eligibleVendorsByKey[key] = data;
    delete state.eligibleLoadingByKey[key];
    delete state.eligibleErrorByKey[key];
  });

  builder.addCase(fetchEligibleVendors().rejected, (state, action) => {
    // Guard FIRST (as every other rejected handler does): a canceled rejection
    // means a superseding dispatch is now in flight and owns this slot's flag —
    // clearing it here would wipe the in-flight loading state out from under it.
    if (isCanceled(action)) return;
    const arg = action.meta?.arg as EligibleArg | undefined;
    if (state.activeMarketUuid && arg?.market_uuid !== state.activeMarketUuid) return;
    if (arg?.problem_category_uuid) {
      const key = eligibleKey(arg.problem_category_uuid, arg.problem_sub_category_uuid);
      delete state.eligibleLoadingByKey[key];
      // Terminal for this slot: with neither data nor a loading flag the fetch
      // effect's "missing" predicate would be true again and re-dispatch forever.
      // The sentinel takes the slot out of that predicate so retry is explicit.
      state.eligibleErrorByKey[key] = true;
    }
    state.eligibleError = errorFromPayload(action.payload);
  });

  // ---- writes: shared saving lifecycle ------------------------------------
  // All four writes share the same pending/rejected handling (see
  // attachSavingLifecycle); only the fulfilled state update differs, so each is
  // expressed as one call passing just that unique body. The `meta.arg` is typed
  // via the helper's `Arg` generic — `WriteArg` here — so the callbacks read it
  // without a local cast.

  // setCategoryDefault: replaces the category's default ranking. Status follows
  // the list's content — clearing the last vendor reverts the category to
  // pending, matching how the BE reads an empty default (APX-447/APX-449). The
  // write saves the market's OWN list, so a trade that was using the company
  // list stops inheriting it (6.6).
  attachSavingLifecycle<VendorPreferenceListResource, WriteArg>(
    builder,
    setCategoryDefault(),
    (state, action) => {
      const arg = action.meta?.arg;
      const resource = action.payload;
      if (!arg?.problem_category_uuid || !resource) return;
      const category = state.categoriesById[arg.problem_category_uuid];
      if (!category) return;
      category.defaultRanking = resource.vendors ?? [];
      category.status = category.defaultRanking.length > 0 ? 'configured' : 'pending';
      category.defaultSource = 'market';
      recomputePendingCount(state);
    },
  );

  // setSubCategoryOverride: replaces a sub's ranking and flips it to override.
  attachSavingLifecycle<VendorPreferenceListResource, WriteArg>(
    builder,
    setSubCategoryOverride(),
    (state, action) => {
      const arg = action.meta?.arg;
      const resource = action.payload;
      if (!arg?.problem_category_uuid || !arg.problem_sub_category_uuid || !resource) return;
      const sub =
        state.subCategoriesById[subKey(arg.problem_category_uuid, arg.problem_sub_category_uuid)];
      if (!sub) return;
      sub.ranking = resource.vendors ?? [];
      sub.state = 'override';
      recomputeOverrideCount(state, arg.problem_category_uuid);
    },
  );

  // customizeSubCategory: opens a sub for editing as an override, seeded with
  // the returned ranking.
  attachSavingLifecycle<VendorPreferenceListResource, WriteArg>(
    builder,
    customizeSubCategory(),
    (state, action) => {
      const arg = action.meta?.arg;
      const resource = action.payload;
      if (!arg?.problem_category_uuid || !arg.problem_sub_category_uuid || !resource) return;
      const sub =
        state.subCategoriesById[subKey(arg.problem_category_uuid, arg.problem_sub_category_uuid)];
      if (!sub) return;
      sub.ranking = resource.vendors ?? [];
      sub.state = 'override';
      recomputeOverrideCount(state, arg.problem_category_uuid);
    },
  );

  // resetSubCategoryToDefault: returns a MESSAGE, not a list. The sub reverts to
  // inheriting its category default, so clear its ranking and mark it inherits.
  attachSavingLifecycle<{ message?: string }, WriteArg>(
    builder,
    resetSubCategoryToDefault(),
    (state, action) => {
      const arg = action.meta?.arg;
      if (!arg?.problem_category_uuid || !arg.problem_sub_category_uuid) return;
      const sub =
        state.subCategoriesById[subKey(arg.problem_category_uuid, arg.problem_sub_category_uuid)];
      if (!sub) return;
      sub.state = 'inherits';
      sub.ranking = [];
      recomputeOverrideCount(state, arg.problem_category_uuid);
    },
  );

  // ---- Company (all markets): its own state, its own lifecycle ------------
  companyExtraReducers(builder);
};

export default extraReducers;
